import { c as _c } from "react/compiler-runtime";
import { useMutation } from '@apollo/client/react';
import { Button, Center, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { AFTER_SUBMIT_STEPS_LIVE, AFTER_SUBMIT_STEPS_PENDING } from '@src/components/sco/Advisors/data/becomeAdvisor';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useAdvisorsAccess } from '@src/components/sco/Advisors/model/access';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { APPLICATION_STEPS, ApplicationStepper, ApplicationStepKey } from '@src/components/sco/Advisors/sections/advisorApplication/ApplicationStepper';
import { AboutYouStep, AdvisingStep, BackgroundStep, ExpertiseStep } from '@src/components/sco/Advisors/sections/advisorApplication/ApplicationSteps';
import { ApplyQuickFill } from '@src/components/sco/Advisors/sections/advisorApplication/ApplyQuickFill';
import { ApplicationReview, ApplicationSubmitted } from '@src/components/sco/Advisors/sections/advisorApplication/ReviewStep';
import { fieldErrorsForStep, hasFieldErrors, useAdvisorApplication } from '@src/components/sco/Advisors/sections/advisorApplication/useAdvisorApplication';
import { SectionScroll } from '@src/components/sco/Advisors/shared/SectionScroll';
import { isAdvisorProfileHubEligible } from '@src/components/sco/Advisors/utils/advisorProfileHub';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_PROFILE_STATUS_APPLIED, ADVISOR_PROFILE_STATUS_APPROVED, ADVISOR_PROFILE_STATUS_REJECTED, ADVISOR_PROFILE_STATUS_SUSPENDED } from '@src/constants/advisor-profile-statuses';
import { EXTRACT_ADVISOR_APPLICATION_FROM_BIO } from '@src/gql/mutations/ExtractAdvisorApplicationFromBio';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { prettyErrorMessage } from '@src/lib/mutationErrors';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { LuArrowLeft, LuArrowRight, LuCheck, LuSave } from 'react-icons/lu';
type AdvisorApplicationSectionProps = {
  /** `apply` = first-time application (wizard); `manage` = edit a live profile (sections). */
  variant: 'apply' | 'manage';
  /** Hide the page title when the hub layout already provides one. */
  hidePageHeader?: boolean;
};

/**
 * Advisor application / profile editor.
 *
 * Four form steps (About You → Expertise → Background → How you advise). Apply
 * walks them in order, then shows Review as an interstitial. Editing an approved
 * profile opens any step directly and saves per step.
 */
export const AdvisorApplicationSection = t0 => {
  const $ = _c(165);
  const {
    variant,
    hidePageHeader: t1
  } = t0;
  const hidePageHeader = t1 === undefined ? false : t1;
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const {
    currentUser
  } = useCurrentUser();
  const {
    canAutoApproveAdvisorApplication,
    wilUpgradeHref
  } = useAdvisorsAccess();
  const application = useAdvisorApplication();
  const {
    values,
    aboutYou,
    aboutHydrated,
    setValue,
    setAboutValue,
    setLocation,
    setCategories,
    suggestedSkills,
    stepComplete,
    percentComplete,
    save,
    saveAboutYou,
    applyExtracted,
    resubmitForReview,
    saving,
    error,
    setError,
    loading,
    isApproved,
    statusKey
  } = application;
  const [activeKey, setActiveKey] = useState("about");
  const [furthestIndex, setFurthestIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [savedNotice, setSavedNotice] = useState(false);
  const [editingPending, setEditingPending] = useState(false);
  const [submittedStatusKey, setSubmittedStatusKey] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [showStepErrors, setShowStepErrors] = useState(false);
  const [errorRevealTick, setErrorRevealTick] = useState(0);
  const [extractApplication, extractMutation] = useMutation(EXTRACT_ADVISOR_APPLICATION_FROM_BIO);
  const mode = variant === "manage" ? "sections" : "wizard";
  let t2;
  if ($[0] !== activeKey) {
    t2 = step => step.key === activeKey;
    $[0] = activeKey;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const activeIndex = APPLICATION_STEPS.findIndex(t2);
  let t3;
  if ($[2] !== statusKey) {
    t3 = isAdvisorProfileHubEligible(statusKey);
    $[2] = statusKey;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const hubEligible = t3;
  const redirectToOwnProfile = variant === "apply" && isApproved && !submitted && !loading;
  const redirectToApply = variant === "manage" && !hubEligible && !loading;
  let t4;
  let t5;
  if ($[4] !== paths.ownProfile || $[5] !== redirectToOwnProfile || $[6] !== router) {
    t4 = () => {
      if (redirectToOwnProfile) {
        router.replace(paths.ownProfile);
      }
    };
    t5 = [redirectToOwnProfile, router, paths.ownProfile];
    $[4] = paths.ownProfile;
    $[5] = redirectToOwnProfile;
    $[6] = router;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] !== paths.apply || $[10] !== redirectToApply || $[11] !== router) {
    t6 = () => {
      if (redirectToApply) {
        router.replace(paths.apply);
      }
    };
    t7 = [redirectToApply, router, paths.apply];
    $[9] = paths.apply;
    $[10] = redirectToApply;
    $[11] = router;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  const t8 = variant === "apply" ? "Apply to advise" : "Advisor profile";
  const t9 = variant === "apply" ? paths.apply : paths.ownProfile;
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = advisorsBreadcrumbsWithTrail({
      title: t8,
      href: t9
    });
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10) {
    t11 = {
      breadcrumbs: t10
    };
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  useAdvisorsToolbar(t11);
  let t12;
  if ($[19] !== aboutYou || $[20] !== activeKey || $[21] !== values) {
    t12 = fieldErrorsForStep(activeKey, aboutYou, values);
    $[19] = aboutYou;
    $[20] = activeKey;
    $[21] = values;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const currentStepErrors = t12;
  let t13;
  if ($[23] !== currentStepErrors || $[24] !== showStepErrors) {
    t13 = showStepErrors ? currentStepErrors : {};
    $[23] = currentStepErrors;
    $[24] = showStepErrors;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const shownStepErrors = t13;
  let t14;
  let t15;
  if ($[26] !== errorRevealTick) {
    t14 = () => {
      if (!errorRevealTick) {
        return;
      }
      const node = document.querySelector("[data-invalid]");
      if (node instanceof HTMLElement) {
        node.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
    };
    t15 = [errorRevealTick];
    $[26] = errorRevealTick;
    $[27] = t14;
    $[28] = t15;
  } else {
    t14 = $[27];
    t15 = $[28];
  }
  useEffect(t14, t15);
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      setShowStepErrors(true);
      setErrorRevealTick(_temp);
    };
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const revealCurrentStepErrors = t16;
  let t17;
  if ($[30] !== currentStepErrors) {
    t17 = () => {
      if (!hasFieldErrors(currentStepErrors)) {
        setShowStepErrors(false);
        return true;
      }
      revealCurrentStepErrors();
      return false;
    };
    $[30] = currentStepErrors;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const currentStepIsValid = t17;
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = key => {
      setReviewing(false);
      setActiveKey(key);
      setSavedNotice(false);
      setShowStepErrors(false);
      const index = APPLICATION_STEPS.findIndex(step_0 => step_0.key === key);
      setFurthestIndex(prev => Math.max(prev, index));
    };
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const goTo = t18;
  let t19;
  if ($[33] !== activeIndex || $[34] !== currentStepIsValid || $[35] !== mode) {
    t19 = key_0 => {
      const targetIndex = APPLICATION_STEPS.findIndex(step_1 => step_1.key === key_0);
      if (mode === "wizard" && targetIndex > activeIndex && !currentStepIsValid()) {
        return;
      }
      goTo(key_0);
    };
    $[33] = activeIndex;
    $[34] = currentStepIsValid;
    $[35] = mode;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const tryGoTo = t19;
  let t20;
  if ($[37] !== applyExtracted || $[38] !== extractApplication || $[39] !== setError) {
    t20 = async input => {
      setError(null);
      const result = await extractApplication({
        variables: {
          input
        }
      });
      if (result.error) {
        throw new Error(prettyErrorMessage(result.error) || "Could not extract an advisor profile from that input.");
      }
      const extracted = result.data?.extractAdvisorApplicationFromBio;
      if (!extracted) {
        throw new Error("Could not extract an advisor profile from that input.");
      }
      const user = extracted.user;
      await applyExtracted({
        headline: extracted.headline || user?.shortBio,
        categories: extracted.categories,
        skills: extracted.skills,
        experiences: extracted.userExperiences,
        firstName: user?.firstName,
        lastName: user?.lastName,
        location: user?.location
      });
      const expertiseReady = !!(extracted.categories?.length || extracted.skills?.length);
      const backgroundReady = !!(extracted.headline || extracted.userExperiences?.length);
      if (backgroundReady) {
        setFurthestIndex(_temp2);
      } else {
        if (expertiseReady) {
          setFurthestIndex(_temp3);
        }
      }
    };
    $[37] = applyExtracted;
    $[38] = extractApplication;
    $[39] = setError;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const handleQuickFill = t20;
  let t21;
  if ($[41] !== setCategories || $[42] !== setValue || $[43] !== shownStepErrors || $[44] !== suggestedSkills || $[45] !== values) {
    t21 = {
      values,
      setValue,
      setCategories,
      suggestedSkills,
      errors: shownStepErrors
    };
    $[41] = setCategories;
    $[42] = setValue;
    $[43] = shownStepErrors;
    $[44] = suggestedSkills;
    $[45] = values;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const stepProps = t21;
  let t22;
  if ($[47] !== aboutHydrated || $[48] !== aboutYou || $[49] !== activeKey || $[50] !== currentUser || $[51] !== setAboutValue || $[52] !== setError || $[53] !== setLocation || $[54] !== shownStepErrors || $[55] !== stepProps) {
    t22 = () => {
      switch (activeKey) {
        case "about":
          {
            return <AboutYouStep aboutHydrated={aboutHydrated} currentUser={currentUser} errors={shownStepErrors} setAboutValue={setAboutValue} setLocation={setLocation} values={aboutYou} onUploadError={message => setError(message)} />;
          }
        case "expertise":
          {
            return <ExpertiseStep {...stepProps} />;
          }
        case "background":
          {
            return <BackgroundStep {...stepProps} />;
          }
        case "advising":
          {
            return <AdvisingStep {...stepProps} />;
          }
      }
    };
    $[47] = aboutHydrated;
    $[48] = aboutYou;
    $[49] = activeKey;
    $[50] = currentUser;
    $[51] = setAboutValue;
    $[52] = setError;
    $[53] = setLocation;
    $[54] = shownStepErrors;
    $[55] = stepProps;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  const renderStep = t22;
  if (loading || redirectToOwnProfile || redirectToApply) {
    let t23;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <Center height="full" padding={8}><Spinner size="xl" /></Center>;
      $[57] = t23;
    } else {
      t23 = $[57];
    }
    return t23;
  }
  const resolvedStatus = isApproved || statusKey === ADVISOR_PROFILE_STATUS_APPROVED || submittedStatusKey === ADVISOR_PROFILE_STATUS_APPROVED ? ADVISOR_PROFILE_STATUS_APPROVED : submittedStatusKey || statusKey;
  const isPending = resolvedStatus === ADVISOR_PROFILE_STATUS_APPLIED;
  const isSuspended = statusKey === ADVISOR_PROFILE_STATUS_SUSPENDED;
  const showPendingConfirmation = variant === "apply" && isPending && !editingPending;
  const showLiveConfirmation = variant === "apply" && resolvedStatus === ADVISOR_PROFILE_STATUS_APPROVED && (submitted || isApproved);
  if (isSuspended) {
    let t23;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <SectionScroll maxWidth="2xl"><VStack align="stretch" gap={3} paddingY={8} textAlign="center"><Text fontWeight="semibold">This advisor profile is suspended</Text><Text color="colorPalette.fg/70" fontFamily="longform">An admin will need to restore it before you can apply again.</Text></VStack></SectionScroll>;
      $[58] = t23;
    } else {
      t23 = $[58];
    }
    return t23;
  }
  if (showLiveConfirmation || showPendingConfirmation) {
    const pending = showPendingConfirmation;
    const t23 = pending ? "pending" : "live";
    const t24 = pending ? AFTER_SUBMIT_STEPS_PENDING : AFTER_SUBMIT_STEPS_LIVE;
    let t25;
    if ($[59] !== canAutoApproveAdvisorApplication || $[60] !== paths.base || $[61] !== paths.opportunities || $[62] !== paths.ownProfile || $[63] !== paths.profileEdit || $[64] !== paths.profileSettings || $[65] !== pending || $[66] !== resubmitForReview || $[67] !== saving || $[68] !== setError || $[69] !== wilUpgradeHref) {
      t25 = pending ? <>{canAutoApproveAdvisorApplication ? <Button loading={saving} variant="cta" onClick={() => {
          (async () => {
            const result_0 = await resubmitForReview();
            if (!result_0.ok) {
              return;
            }
            setSubmitted(true);
            setSubmittedStatusKey(result_0.statusKey);
            setEditingPending(false);
            if (result_0.statusKey !== ADVISOR_PROFILE_STATUS_APPROVED) {
              setError("Your application is still in review. Refresh after your $49 plan is active, then try again.");
            }
          })();
        }}>Submit and go live</Button> : <Button asChild={true} variant="cta"><NextLink href={wilUpgradeHref}>Upgrade to Startups Pro</NextLink></Button>}<Button variant="outline" onClick={() => setEditingPending(true)}>Update application</Button><Button asChild={true} variant="outline"><NextLink href={paths.ownProfile}>My advisor profile</NextLink></Button><Button asChild={true} variant="ghost"><NextLink href={paths.base}>Back to Advisors</NextLink></Button></> : <><Button asChild={true} variant="cta"><NextLink href={paths.ownProfile}>Continue setup</NextLink></Button><Button asChild={true} variant="outline"><NextLink href={paths.opportunities}>View opportunities</NextLink></Button><Button asChild={true} variant="outline"><NextLink href={paths.profileEdit}>Edit profile</NextLink></Button><Button asChild={true} variant="ghost"><NextLink href={paths.profileSettings}>Session rates</NextLink></Button></>;
      $[59] = canAutoApproveAdvisorApplication;
      $[60] = paths.base;
      $[61] = paths.opportunities;
      $[62] = paths.ownProfile;
      $[63] = paths.profileEdit;
      $[64] = paths.profileSettings;
      $[65] = pending;
      $[66] = resubmitForReview;
      $[67] = saving;
      $[68] = setError;
      $[69] = wilUpgradeHref;
      $[70] = t25;
    } else {
      t25 = $[70];
    }
    let t26;
    if ($[71] !== error || $[72] !== t23 || $[73] !== t24 || $[74] !== t25) {
      t26 = <SectionScroll maxWidth="2xl"><ApplicationSubmitted error={error} mode={t23} steps={t24} actions={t25} /></SectionScroll>;
      $[71] = error;
      $[72] = t23;
      $[73] = t24;
      $[74] = t25;
      $[75] = t26;
    } else {
      t26 = $[75];
    }
    return t26;
  }
  const isLastFormStep = activeKey === "advising";
  const showReview = variant === "apply" && reviewing;
  let t23;
  if ($[76] !== activeKey || $[77] !== save || $[78] !== saveAboutYou) {
    t23 = async () => {
      const result_1 = activeKey === "about" ? await saveAboutYou() : await save({
        submitForReview: false
      });
      setSavedNotice(result_1.ok);
    };
    $[76] = activeKey;
    $[77] = save;
    $[78] = saveAboutYou;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  const handleSaveStep = t23;
  let t24;
  if ($[80] !== activeIndex || $[81] !== activeKey || $[82] !== currentStepIsValid || $[83] !== isLastFormStep || $[84] !== save || $[85] !== saveAboutYou || $[86] !== variant) {
    t24 = async () => {
      if (!currentStepIsValid()) {
        return;
      }
      const result_2 = activeKey === "about" ? await saveAboutYou() : await save({
        submitForReview: false
      });
      if (!result_2.ok) {
        return;
      }
      if (variant === "apply" && isLastFormStep) {
        setReviewing(true);
        setSavedNotice(false);
        setShowStepErrors(false);
        return;
      }
      goTo(APPLICATION_STEPS[Math.min(activeIndex + 1, APPLICATION_STEPS.length - 1)].key);
    };
    $[80] = activeIndex;
    $[81] = activeKey;
    $[82] = currentStepIsValid;
    $[83] = isLastFormStep;
    $[84] = save;
    $[85] = saveAboutYou;
    $[86] = variant;
    $[87] = t24;
  } else {
    t24 = $[87];
  }
  const handleContinue = t24;
  let t25;
  if ($[88] !== save || $[89] !== stepComplete) {
    t25 = async () => {
      const result_3 = await save({
        submitForReview: true
      });
      if (result_3.ok) {
        setSubmitted(true);
        setSubmittedStatusKey(result_3.statusKey);
        setEditingPending(false);
        setReviewing(false);
        return;
      }
      const firstIncomplete = APPLICATION_STEPS.find(step_2 => !stepComplete[step_2.key]);
      if (firstIncomplete) {
        setReviewing(false);
        setActiveKey(firstIncomplete.key);
        setShowStepErrors(true);
        setErrorRevealTick(_temp4);
      }
    };
    $[88] = save;
    $[89] = stepComplete;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  const handleSubmit = t25;
  if (showReview) {
    const t26 = canAutoApproveAdvisorApplication ? "Submit and go live" : "Submit for review";
    let t27;
    if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = () => setReviewing(false);
      $[91] = t27;
    } else {
      t27 = $[91];
    }
    let t28;
    if ($[92] !== handleSubmit) {
      t28 = () => void handleSubmit();
      $[92] = handleSubmit;
      $[93] = t28;
    } else {
      t28 = $[93];
    }
    let t29;
    if ($[94] !== aboutYou || $[95] !== currentUser || $[96] !== error || $[97] !== saving || $[98] !== t26 || $[99] !== t28 || $[100] !== values) {
      t29 = <SectionScroll maxWidth="2xl"><ApplicationReview aboutYou={aboutYou} currentUser={currentUser} error={error} saving={saving} submitLabel={t26} values={values} onBack={t27} onSubmit={t28} /></SectionScroll>;
      $[94] = aboutYou;
      $[95] = currentUser;
      $[96] = error;
      $[97] = saving;
      $[98] = t26;
      $[99] = t28;
      $[100] = values;
      $[101] = t29;
    } else {
      t29 = $[101];
    }
    return t29;
  }
  let t26;
  if ($[102] !== hidePageHeader || $[103] !== variant) {
    t26 = !hidePageHeader && <VStack align="stretch" gap={1}><Text fontSize="2xl" fontWeight="semibold">{variant === "apply" ? "Apply to advise" : "Edit Your Advisor Profile"}</Text><Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">{variant === "apply" ? null : "Jump to any section and save when you are done."}</Text></VStack>;
    $[102] = hidePageHeader;
    $[103] = variant;
    $[104] = t26;
  } else {
    t26 = $[104];
  }
  let t27;
  if ($[105] !== statusKey) {
    t27 = statusKey === ADVISOR_PROFILE_STATUS_REJECTED && <Text color="orange.fg" fontFamily="longform" fontSize="sm">Your previous application was not approved. Update it and resubmit.</Text>;
    $[105] = statusKey;
    $[106] = t27;
  } else {
    t27 = $[106];
  }
  let t28;
  if ($[107] !== activeKey || $[108] !== furthestIndex || $[109] !== mode || $[110] !== percentComplete || $[111] !== stepComplete || $[112] !== tryGoTo) {
    t28 = <ApplicationStepper activeKey={activeKey} complete={stepComplete} furthestIndex={furthestIndex} mode={mode} percentComplete={percentComplete} onSelect={tryGoTo} />;
    $[107] = activeKey;
    $[108] = furthestIndex;
    $[109] = mode;
    $[110] = percentComplete;
    $[111] = stepComplete;
    $[112] = tryGoTo;
    $[113] = t28;
  } else {
    t28 = $[113];
  }
  let t29;
  if ($[114] !== activeKey || $[115] !== currentUser?.userId || $[116] !== extractMutation || $[117] !== handleQuickFill || $[118] !== variant) {
    t29 = variant === "apply" && activeKey === "about" && <ApplyQuickFill currentUserId={currentUser?.userId} extracting={extractMutation.loading} onExtract={input_0 => handleQuickFill(input_0)} />;
    $[114] = activeKey;
    $[115] = currentUser?.userId;
    $[116] = extractMutation;
    $[117] = handleQuickFill;
    $[118] = variant;
    $[119] = t29;
  } else {
    t29 = $[119];
  }
  let t30;
  if ($[120] !== renderStep) {
    t30 = renderStep();
    $[120] = renderStep;
    $[121] = t30;
  } else {
    t30 = $[121];
  }
  let t31;
  if ($[122] !== error) {
    t31 = error && <Text color="red.fg" fontFamily="longform" fontSize="sm">{error}</Text>;
    $[122] = error;
    $[123] = t31;
  } else {
    t31 = $[123];
  }
  let t32;
  if ($[124] !== error || $[125] !== savedNotice) {
    t32 = savedNotice && !error && <Text color="green.fg" fontFamily="longform" fontSize="sm">Saved.</Text>;
    $[124] = error;
    $[125] = savedNotice;
    $[126] = t32;
  } else {
    t32 = $[126];
  }
  const t33 = activeIndex === 0;
  let t34;
  if ($[127] !== activeIndex) {
    t34 = () => goTo(APPLICATION_STEPS[Math.max(activeIndex - 1, 0)].key);
    $[127] = activeIndex;
    $[128] = t34;
  } else {
    t34 = $[128];
  }
  let t35;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <LuArrowLeft />;
    $[129] = t35;
  } else {
    t35 = $[129];
  }
  let t36;
  if ($[130] !== t33 || $[131] !== t34) {
    t36 = <Button disabled={t33} variant="ghost" onClick={t34}>{t35} Back</Button>;
    $[130] = t33;
    $[131] = t34;
    $[132] = t36;
  } else {
    t36 = $[132];
  }
  let t37;
  if ($[133] !== handleSaveStep || $[134] !== isLastFormStep || $[135] !== mode || $[136] !== saving) {
    t37 = mode === "sections" && !isLastFormStep && <Button loading={saving} variant="outline" onClick={() => void handleSaveStep()}><LuSave /> Save</Button>;
    $[133] = handleSaveStep;
    $[134] = isLastFormStep;
    $[135] = mode;
    $[136] = saving;
    $[137] = t37;
  } else {
    t37 = $[137];
  }
  let t38;
  if ($[138] !== handleContinue || $[139] !== handleSaveStep || $[140] !== handleSubmit || $[141] !== isLastFormStep || $[142] !== mode || $[143] !== saving || $[144] !== statusKey || $[145] !== variant) {
    t38 = mode === "sections" && isLastFormStep ? <><Button loading={saving} variant="outline" onClick={() => void handleSaveStep()}><LuSave /> Save profile</Button>{statusKey === ADVISOR_PROFILE_STATUS_REJECTED && <Button loading={saving} variant="cta" onClick={() => void handleSubmit()}><LuCheck /> Resubmit for review</Button>}</> : <Button loading={saving} variant="cta" onClick={() => void handleContinue()}>{variant === "apply" && isLastFormStep ? "Review application" : "Continue"} <LuArrowRight /></Button>;
    $[138] = handleContinue;
    $[139] = handleSaveStep;
    $[140] = handleSubmit;
    $[141] = isLastFormStep;
    $[142] = mode;
    $[143] = saving;
    $[144] = statusKey;
    $[145] = variant;
    $[146] = t38;
  } else {
    t38 = $[146];
  }
  let t39;
  if ($[147] !== t37 || $[148] !== t38) {
    t39 = <HStack gap={3} wrap="wrap">{t37}{t38}</HStack>;
    $[147] = t37;
    $[148] = t38;
    $[149] = t39;
  } else {
    t39 = $[149];
  }
  let t40;
  if ($[150] !== t36 || $[151] !== t39) {
    t40 = <HStack gap={3} justify="space-between" wrap="wrap">{t36}{t39}</HStack>;
    $[150] = t36;
    $[151] = t39;
    $[152] = t40;
  } else {
    t40 = $[152];
  }
  let t41;
  if ($[153] !== variant) {
    t41 = variant === "apply" && <Text color="colorPalette.fg/50" fontSize="xs">Your answers save as you go — you can come back and finish later.</Text>;
    $[153] = variant;
    $[154] = t41;
  } else {
    t41 = $[154];
  }
  let t42;
  if ($[155] !== t26 || $[156] !== t27 || $[157] !== t28 || $[158] !== t29 || $[159] !== t30 || $[160] !== t31 || $[161] !== t32 || $[162] !== t40 || $[163] !== t41) {
    t42 = <SectionScroll maxWidth="5xl">{t26}{t27}{t28}{t29}{t30}{t31}{t32}{t40}{t41}</SectionScroll>;
    $[155] = t26;
    $[156] = t27;
    $[157] = t28;
    $[158] = t29;
    $[159] = t30;
    $[160] = t31;
    $[161] = t32;
    $[162] = t40;
    $[163] = t41;
    $[164] = t42;
  } else {
    t42 = $[164];
  }
  return t42;
};
function _temp(tick) {
  return tick + 1;
}
function _temp2(prev_0) {
  return Math.max(prev_0, 2);
}
function _temp3(prev_1) {
  return Math.max(prev_1, 1);
}
function _temp4(tick_0) {
  return tick_0 + 1;
}